
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useState, useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/business`,
  url: `${siteUrl}/business`,
  name: '事業内容 | 株式会社鈴電エンジニア',
  description:
    '電気設備工事・消防設備工事・空調設備工事・管工事など、株式会社鈴電エンジニアが手がける幅広い設備工事をご紹介します。',
  inLanguage: 'ja',
  isPartOf: { '@id': `${siteUrl}/#website` },
  about: { '@id': `${siteUrl}/#organization` },
};

export default function BusinessPage() {
  const mainAnimation = useScrollAnimation();
  const otherAnimation = useScrollAnimation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const electricalImages = [
    {
      src: 'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/784dce7e0eeb7a638856b39d458f2d0b.jpeg',
      alt: '電気設備工事'
    },
    {
      src: 'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/f86d795fcf6a84efa5d077c94fb3bc8b.jpeg',
      alt: '電気設備工事 施工例'
    },
    {
      src: 'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/a4da772be1b7746cd4733d8c963e8139.jpeg',
      alt: '電気設備工事 施工例2'
    },
    {
      src: 'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/824e9a88ff8aed65b67781a2e1d402a1.jpeg',
      alt: '太陽光発電設備工事'
    }
  ];

  useEffect(() => {
    // SEO meta
    document.title = '事業内容 | 株式会社鈴電エンジニア';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '電気設備工事・消防設備工事・空調設備工事・管工事など、株式会社鈴電エンジニアが手がける幅広い設備工事をご紹介します。');

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-business';
    script.text = JSON.stringify(schemaJsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-business');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % electricalImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [electricalImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % electricalImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + electricalImages.length) % electricalImages.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/de81cd3d120ced2b574d4061ce2d32b9.jpeg"
            alt="事業内容"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">事業内容</h1>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-left sm:text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">確かな技術力で、安全と快適な環境を支える</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            当社は、電気工事を中心に、消防設備工事・保守点検、空調設備工事、<br />
            管工事、電気通信工事など幅広い分野でサービスを提供しています。
          </p>
        </div>
      </section>

      {/* Main Business - Electrical Work */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div 
            ref={mainAnimation.ref as React.RefObject<HTMLDivElement>}
            className={`scroll-animate ${mainAnimation.isVisible ? 'is-visible' : ''}`}
          >
            <div className="mb-12">
              <div className="w-12 h-0.5 bg-blue-600 mb-3"></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">電気設備工事</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  毎日の生活を安全で快適にするためには、適切に設計・施工された電気設備は欠かせません。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  当社では住宅等の低圧電気設備から工場等の高圧受変電設備まで、電気設備の設計・施工を一貫して行ってきた経験と実績があります。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  現場ごとに異なる条件やご要望に柔軟に対応し、電気を通じてお客様の思いや課題に寄り添う施工を大切にしています。
                </p>

                <div className="bg-blue-600 text-white p-8 rounded-lg">
                  <h3 className="text-xl font-bold mb-6 border-b-2 border-white pb-2 inline-block">
                    &lt;対応工事例&gt;
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        <span className="text-base">電気配管・配線工事</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        <span className="text-base">照明設備工事</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        <span className="text-base">受変電設備改修工事</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        <span className="text-base">太陽光発電設備工事</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-400">•</span>
                        <span className="text-base">通信設備工事</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="w-full h-[400px] sm:h-[500px] relative overflow-hidden rounded-lg shadow-lg">
                  {electricalImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                        index === currentSlide 
                          ? 'opacity-100 translate-x-0' 
                          : index < currentSlide 
                            ? 'opacity-0 -translate-x-full' 
                            : 'opacity-0 translate-x-full'
                      }`}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}

                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-300 cursor-pointer z-10"
                    aria-label="前の画像"
                  >
                    <i className="ri-arrow-left-s-line text-2xl text-gray-800"></i>
                  </button>

                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-300 cursor-pointer z-10"
                    aria-label="次の画像"
                  >
                    <i className="ri-arrow-right-s-line text-2xl text-gray-800"></i>
                  </button>

                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {electricalImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          index === currentSlide 
                            ? 'bg-white w-8' 
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`画像 ${index + 1}へ`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div 
            ref={otherAnimation.ref as React.RefObject<HTMLDivElement>}
            className={`scroll-animate ${otherAnimation.isVisible ? 'is-visible' : ''}`}
          >
            <div className="mb-12">
              <div className="w-12 h-0.5 bg-blue-600 mb-3"></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">その他の設備工事</h2>
              <p className="text-gray-700 mt-4 leading-relaxed">
                電気設備工事以外にも、幅広い設備工事に対応しています。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/c138e34c9aba122ff3795d906cd1ea56.jpeg"
                    alt="消防設備工事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">消防設備工事・保守点検</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    火災報知設備、スプリンクラー設備、消火器など、消防法に基づいた設備の設計・施工・点検を行います。
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/ba45ee1a72b4f272ce0016421c8db963.jpeg"
                    alt="空調設備工事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">空調設備工事</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    快適な室内環境を実現するため、エアコンや換気設備の設計・施工を行います。
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/36ff64294cc254d33013a13d4c11044e.jpeg"
                    alt="管工事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">管工事</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    給排水設備、衛生設備など、建物に欠かせない配管工事を行います。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
